import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import { ChatOverlayRoot } from './ChatOverlayRoot';
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';

import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';

import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

import { MOTION, REACTIONS } from './chatTheme';

/**
 * Контекстное меню сообщения: правая кнопка на ПК, долгое нажатие на телефоне.
 *
 * Сверху — быстрые реакции, ниже — действия. «Изменить» и «Удалить» показываем
 * только тогда, когда сервер их реально разрешит (своё сообщение, не системное,
 * правка — в пределах суток), чтобы человек не тыкал в пункт и не ловил 403.
 *
 * Рисуется порталом в body и сам разворачивается к центру экрана, если
 * не помещается: меню у нижнего края ленты иначе уезжало бы за окно.
 */

const MENU_W = 216;

export default function MessageContextMenu({
  anchor,            // { x, y } — точка вызова
  message,
  canEdit,
  canDelete,
  views,             // { views_count, recipients_total, reactions } или null
  viewsLoading,
  onOpenViews,
  onReply,
  onEdit,
  onDelete,
  onReact,
  onTogglePin,
  onClose,
  onSelectFiles,
}) {
  const portalRoot = useContext(ChatOverlayRoot);
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: anchor.x, top: anchor.y, origin: 'top left' });

  // Разворачиваем меню так, чтобы оно целиком помещалось в окно
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const h = el.offsetHeight || 220;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = anchor.x;
    let top = anchor.y;
    let originX = 'left';
    let originY = 'top';
    if (left + MENU_W + 12 > vw) { left = Math.max(12, left - MENU_W); originX = 'right'; }
    if (top + h + 12 > vh) { top = Math.max(12, top - h); originY = 'bottom'; }
    setPos({ left, top, origin: `${originY} ${originX}` });
  }, [anchor.x, anchor.y]);

  // Закрытие: клик мимо, Esc, прокрутка, смена размера окна
  useEffect(() => {
    const onDown = (e) => { if (!ref.current?.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); } };
    // capture: лента прокручивается во вложенном контейнере, а не в окне
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  const myReactions = new Set(
    (message?.reactions || []).filter((r) => r.mine).map((r) => r.emoji),
  );

  const Item = ({ icon, label, onClick, danger }) => (
    <Box
      component={motion.div}
      whileHover={{ x: 2 }}
      onClick={() => { onClick(); onClose(); }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        px: 1.25, py: 0.875, borderRadius: '10px', cursor: 'pointer',
        color: danger ? 'danger.400' : 'text.primary',
        transition: 'background-color .12s',
        '&:hover': { backgroundColor: danger ? 'danger.softBg' : 'background.level1' },
        '& svg': { fontSize: 18, color: danger ? 'var(--joy-palette-danger-400)' : 'var(--joy-palette-text-tertiary)' },
      }}
    >
      {icon}
      <Typography level="body-sm" sx={{ fontWeight: 550, color: 'inherit' }}>
        {label}
      </Typography>
    </Box>
  );

  return createPortal(
    <Box
      ref={ref}
      component={motion.div}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={MOTION.spring}
      onContextMenu={(e) => e.preventDefault()}
      sx={(theme) => ({
        position: 'fixed',
        left: pos.left,
        top: pos.top,
        transformOrigin: pos.origin,
        width: MENU_W,
        zIndex: 12050,
        p: 0.625,
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        backdropFilter: 'blur(16px)',
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(30,35,50,0.94)'
          : 'rgba(255,255,255,0.96)',
        boxShadow: '0 12px 32px rgba(16,24,40,0.18), 0 2px 8px rgba(16,24,40,0.08)',
        // Меню — набор кнопок, выделять в нём нечего. Без этого касание по
        // пункту на телефоне продолжало выделение, начатое долгим нажатием,
        // и вместо нажатия получалась растяжка синего блока.
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
      })}
    >
      {/* Быстрые реакции */}
      {message?.type_sender !== 'system' && <Box
        sx={{
          display: 'flex', justifyContent: 'space-between', gap: 0.25,
          px: 0.25, pb: 0.625, mb: 0.375,
          borderBottom: '1px solid', borderColor: 'divider',
        }}
      >
        {REACTIONS.map((emoji, i) => (
          <Box
            key={emoji}
            component={motion.button}
            type="button"
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ ...MOTION.spring, delay: i * 0.028 }}
            whileHover={{ scale: 1.28, y: -3 }}
            whileTap={{ scale: 0.85 }}
            onClick={() => { onReact(emoji); onClose(); }}
            sx={{
              border: 'none', cursor: 'pointer',
              width: 30, height: 30, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 17, lineHeight: 1, p: 0,
              backgroundColor: myReactions.has(emoji) ? 'rgba(99,102,241,0.18)' : 'transparent',
              '&:hover': { backgroundColor: 'background.level1' },
            }}
          >
            {emoji}
          </Box>
        ))}
      </Box>}

      {message?.type_sender !== 'system' && <Item icon={<ReplyRoundedIcon />} label="Ответить" onClick={() => onReply(message)} />}
      {onSelectFiles && <Item icon={<ChecklistRoundedIcon/>} label="Выбрать вложения" onClick={() => onSelectFiles(message)}/>}
      {/* Закреп общий для всех участников, поэтому пункт есть у любого
          сообщения, а не только у своего: это пометка «смотри сюда»,
          а не право автора. */}
      {onTogglePin && message?.type_sender !== 'system' && (
        <Item
          icon={message?.is_pinned
            ? <PushPinOutlinedIcon />
            : <PushPinRoundedIcon sx={{ transform: 'rotate(35deg)' }} />}
          label={message?.is_pinned ? 'Открепить' : 'Закрепить'}
          onClick={() => onTogglePin(message)}
        />
      )}
      {canEdit && (
        <Item icon={<EditRoundedIcon />} label="Изменить" onClick={() => onEdit(message)} />
      )}
      {Boolean((message?.content || '').trim()) && (
        <Item
          icon={<ContentCopyRoundedIcon />}
          label="Копировать текст"
          onClick={() => {
            try { navigator.clipboard?.writeText(message.content || ''); } catch (e) { /* буфер недоступен */ }
          }}
        />
      )}
      {canDelete && (
        <Item
          icon={<DeleteOutlineRoundedIcon />}
          label="Удалить"
          danger
          onClick={() => onDelete(message)}
        />
      )}

      {/* Подвал «кто посмотрел» — как в мессенджерах: строка со счётчиком,
          по клику открывается полный список прочитавших и реакций. */}
      {onOpenViews && message?.type_sender !== 'system' && (
        <Box
          onClick={() => { onOpenViews(message); onClose(); }}
          sx={{
            mt: 0.375, pt: 0.625,
            borderTop: '1px solid', borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.25, py: 0.75, borderRadius: '10px', cursor: 'pointer',
            transition: 'background-color .12s',
            '&:hover': { backgroundColor: 'background.level1' },
            '& svg': { fontSize: 17, color: 'var(--joy-palette-text-tertiary)' },
          }}
        >
          <DoneAllRoundedIcon />
          <Typography level="body-sm" sx={{ fontWeight: 550, color: 'text.secondary' }}>
            {viewsLoading
              ? 'Считаем просмотры…'
              : views
                ? `Прочитали ${views.views_count ?? 0} из ${views.recipients_total ?? 0}`
                : 'Просмотры'}
          </Typography>
        </Box>
      )}
    </Box>,
    portalRoot || document.body,
  );
}